import {
  Alert,
  Autocomplete,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  AutocompleteOption as JoyAutocompleteOption,
  ListItemContent,
  ListItemDecorator,
  Typography,
} from '@mui/joy';
import { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  type CompanyCrmAutoCompleteOption,
  type ContactCrmAutoCompleteOption,
  type CrmAutoCompleteOption,
} from '~/common/types';
import { type CompanyFormValues } from '@/containers/Companies/AddEditCompany/useAddEditCompanyFormConfig';
import { type ContactFormValues } from '@/containers/Contacts/AddEditContact/useContactForm';

import { CrmOptionType, CrmType } from '~/common/enums';
import { trpc } from '@/config/trpc';

import {
  type CrmAutocompleteDataOption,
  filterOptions,
  isDataOption,
  type ModalTypes,
  optionsGroupBy,
  sortOptions,
} from '@/shared/components/2.0/select/crm/utils';

import { useCreateCompany, useCreateContact } from '@/mutations';
import useHandleCreateCompanyError from '@/shared/hooks/useHandleCreateCompanyError';
import useToggle, { type UseToggle } from '@/shared/hooks/UseToggle';
import useEvent from '@/shared/hooks/useEvent';
import { useGetCrmOptions } from '@/queries';

import AddCentralCompanyForm, {
  type CreateCentralCompanyFormikValues,
} from '@/containers/CentralCompanies/AddCentralCompanyForm';
import AddEditCompanyForm from '@/containers/Companies/AddEditCompany/AddEditCompanyForm';
import AddEditContactForm from '@/containers/Contacts/AddEditContact/AddEditContactForm';
import CrmTypeIcon from '@/shared/components/2.0/select/crm/CrmTypeIcon';
import { InlineSpinner } from '@/shared/icons/Icons';
import KpcModal from '../../layout/KpcModal';
import SidebarModal from '../../layout/SidebarModal';

type AdapterProps = _BaseSelectCRMJoyProps<
  CrmAutocompleteDataOption | string,
  CrmAutocompleteDataOption[] | string[]
>;

type BaseProps = {
  addInSidebar?: boolean;
  buildingId?: string;
  excludeIds?: string[];
  hasFinancialInfo?: boolean;
  hoaId?: string;
  hoaRelationsOnly?: boolean;
  includeCentralCompanies?: boolean;
  includeCompanies?: boolean;
  includeContacts?: boolean;
  includeTeams?: boolean;
  inputClassName?: string;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  onOptionsLoaded?: (options: CrmAutocompleteDataOption[]) => void;
  placeholder?: string;
  showAddCentralCompany?: boolean;
  showAddCompany?: boolean;
  showAddContact?: boolean;
  startDecorator?: React.ReactNode;
};

type ControlProps = {
  className?: string;
  disabled?: boolean;
  error?: string;
  isRequired?: boolean;
  label?: string;
};

type OptionsRecord = {
  [key: string]: CrmAutocompleteDataOption;
};

type ValueProps<
  TSingle extends CrmAutocompleteDataOption | string = CrmAutocompleteDataOption,
  TMultiple extends CrmAutocompleteDataOption[] | string[] = CrmAutocompleteDataOption[],
> =
  | {
      onChange: (option: CrmAutocompleteDataOption | null) => void;
      multiple?: false;
      value?: TSingle | null;
    }
  | {
      onChange: (option: CrmAutocompleteDataOption[]) => void;
      multiple: true;
      value?: TMultiple;
    };

type WithControlProps<T> = T & ControlProps;

const useIdValueAdapter = (props: AdapterProps): _BaseSelectCRMJoyProps => {
  const { multiple, onChange, onOptionsLoaded, value, ...rest } = props;

  const [optionsById, setOptionsById] = useState<OptionsRecord>();

  const addOption = useCallback((option: CrmAutocompleteDataOption) => {
    setOptionsById((prev) => ({
      ...prev,
      [option.value]: option,
    }));
  }, []);

  const lookupOption = useCallback(
    (query: CrmAutocompleteDataOption | string | null | undefined) => {
      if (!query || !optionsById) return null;

      const id = typeof query === 'string' ? query : query.value;

      return optionsById[id] ?? null;
    },
    [optionsById],
  );

  const lookupOptionsMultiple = useCallback(
    (values: CrmAutocompleteDataOption[] | string[] | undefined) => {
      const selected: CrmAutocompleteDataOption[] = [];

      values?.forEach((subValue) => {
        const option = lookupOption(subValue);

        if (option) selected.push(option);
      });

      return selected;
    },
    [lookupOption],
  );

  const valueMultiple = useMemo(
    () => (multiple ? lookupOptionsMultiple(value) : []),
    [lookupOptionsMultiple, multiple, value],
  );

  const valueSingle = useMemo(
    () => (multiple ? null : lookupOption(value)),
    [lookupOption, multiple, value],
  );

  const onOptionsLoadedInternal = useEvent(onOptionsLoaded);

  const handleOptionsLoaded = useCallback(
    (options: CrmAutocompleteDataOption[]) => {
      const optionEntries = options.map((option) => [option.value, option] as const);
      const optionsRecord = Object.fromEntries(optionEntries);

      onOptionsLoadedInternal(options);
      setOptionsById(optionsRecord);
    },
    [onOptionsLoadedInternal],
  );

  useEffect(() => {
    if (!value) return;
    const values = Array.isArray(value) ? value : [value];

    setOptionsById((prev) => {
      const next = { ...prev };

      values.forEach((v) => {
        if (typeof v !== 'string') {
          next[v.value] = v;
        }
      });
      return next;
    });
  }, [value]);

  return multiple
    ? {
        ...rest,
        addOption,
        multiple,
        onChange,
        onOptionsLoaded: handleOptionsLoaded,
        value: valueMultiple,
      }
    : {
        ...rest,
        addOption,
        multiple,
        onChange,
        onOptionsLoaded: handleOptionsLoaded,
        value: valueSingle,
      };
};

type AddModalProps = React.PropsWithChildren<{
  sidebar: boolean;
  title?: string;
  toggle: UseToggle<ModalTypes>;
}>;

const AddModal: React.FC<AddModalProps> = ({ sidebar, ...rest }) =>
  sidebar ? (
    <SidebarModal
      {...rest}
      hideBackdrop
      showClose
      className="3xl:w-1/3"
      size="lg"
      title={rest.title ?? ''}
    />
  ) : (
    <KpcModal {...rest} sx={{ zIndex: 100000 }} />
  );

type _BaseSelectCRMJoyProps<
  TSingle extends CrmAutocompleteDataOption | string = CrmAutocompleteDataOption,
  TMultiple extends CrmAutocompleteDataOption[] | string[] = CrmAutocompleteDataOption[],
> = BaseProps &
  ValueProps<TSingle, TMultiple> & {
    addOption?: (option: CrmAutocompleteDataOption) => void;
  };

export type BaseSelectCRMJoyProps<T extends boolean = false> = Extract<
  _BaseSelectCRMJoyProps,
  { multiple?: T }
>;

export const BaseSelectCRMJoy: React.FC<_BaseSelectCRMJoyProps> = ({
  addInSidebar = false,
  buildingId,
  excludeIds,
  hasFinancialInfo = false,
  hoaId,
  hoaRelationsOnly = false,
  includeCentralCompanies = false,
  includeCompanies = true,
  includeContacts = true,
  includeTeams = false,
  inputClassName,
  multiple,
  onChange,
  onOptionsLoaded,
  showAddCentralCompany = false,
  showAddCompany = true,
  showAddContact = true,
  value,
  addOption,
  ...rest
}) => {
  const [query, setQuery] = useState('');

  const { crmOptions, areCrmOptionsLoading } = useGetCrmOptions({
    buildingId,
    hasFinancialInfo,
    hoaId,
    hoaRelationsOnly,
    includeCentralCompanies,
    includeCompanies,
    includeContacts,
    includeTeams,
  });
  const { t } = useTranslation();
  const handleCreateCompanyError = useHandleCreateCompanyError();

  const mutations = {
    createCentralCompany: trpc.company.centralCompany.create.useMutation(),
    createCompany: useCreateCompany(),
    createContact: useCreateContact(),
  };

  const modalToggle = useToggle<ModalTypes>();

  const sortedOptions = useMemo(
    () => sortOptions(crmOptions, excludeIds),
    [crmOptions, excludeIds],
  );

  const addCreatedOptionToValue = (newOption: CrmAutocompleteDataOption) => {
    addOption?.(newOption);

    const selectedMultiple = Array.isArray(value);

    if (!multiple) {
      onChange(newOption);

      return;
    }
    if (!selectedMultiple) {
      onChange([newOption]);

      return;
    }

    onChange([...value, newOption]);
  };

  const handleCreateContact = (values: ContactFormValues) => {
    const { addressLine, postalCode, city, country, ...rest } = values;

    const emailAddresses = values.emailAddresses.filter((email) => !!email.email);

    return mutations.createContact.mutateAsync(
      {
        ...rest,
        address: {
          addressLine,
          city,
          country,
          postalCode,
        },
        emailAddresses,
      },
      {
        onSuccess: (data) => {
          const newOption: ContactCrmAutoCompleteOption = {
            crmType: CrmType.Contact,
            data,
            hasDefaultIban: data.ibans.some((i) => i.isDefault),
            label: `${data.firstName || ''} ${data.lastName}`.trim(),
            type: CrmOptionType.Contact,
            value: data.id as string,
          };
          addCreatedOptionToValue(newOption);
          modalToggle.hide();
        },
      },
    );
  };

  const handleCreateCompany = async (values: CompanyFormValues) => {
    try {
      await mutations.createCompany.mutateAsync(values, {
        onSuccess: (data) => {
          const newOption: CompanyCrmAutoCompleteOption = {
            crmType: CrmType.Company,
            data,
            hasDefaultIban: data.ibans.some((i) => i.isDefault),
            label: data.displayName ?? values.name ?? '',
            type: CrmOptionType.Company,
            value: data.id as string,
          };

          addCreatedOptionToValue(newOption);
          modalToggle.hide();
        },
      });
    } catch (error) {
      handleCreateCompanyError(error, values.vatNumber);
    }
  };

  const handleCreateCentralCompany = async (centralCompany: CreateCentralCompanyFormikValues) => {
    const data = await mutations.createCentralCompany.mutateAsync({
      vatNumber: centralCompany.vatNumber,
    });

    addCreatedOptionToValue({
      crmType: CrmType.CentralCompany,
      data,
      hasDefaultIban: false,
      label: `${centralCompany.name} (${centralCompany.vatNumber})`,
      type: CrmOptionType.CentralCompany,
      value: data.id,
    });
  };

  const handleChange = (newValue: CrmAutoCompleteOption | null | CrmAutoCompleteOption[]) => {
    const valueIsMultiple = Array.isArray(newValue);

    if (multiple) {
      if (valueIsMultiple) {
        const valueDataOptions = newValue.filter(isDataOption);

        onChange(valueDataOptions);
      } else {
        onChange([]);
      }

      return;
    }

    if (!valueIsMultiple && isDataOption(newValue)) onChange(newValue);
    else onChange(null);
  };

  const modalTitles: { [key: ModalTypes]: string } = {
    centralCompany: t('addType', { type: t('centralCompany') }),
    company: t('addType', { type: t('_company.title') }),
    contact: t('addType', { type: t('_contact.title') }),
  };

  const handleOptionsLoaded = useEvent(() => {
    if (!onOptionsLoaded) return;

    const dataOptions = sortedOptions.filter(isDataOption);

    onOptionsLoaded(dataOptions);
  });

  useEffect(() => {
    if (areCrmOptionsLoading) return;

    handleOptionsLoaded();
  }, [areCrmOptionsLoading, handleOptionsLoaded]);

  return (
    <>
      <Autocomplete
        {...rest}
        className={inputClassName}
        disableCloseOnSelect={multiple}
        endDecorator={areCrmOptionsLoading && <InlineSpinner />}
        filterOptions={(options, params) =>
          filterOptions(
            options,
            sortedOptions,
            modalToggle,
            params,
            query,
            areCrmOptionsLoading,
            showAddCentralCompany,
            showAddCompany,
            showAddContact,
            value,
            t,
          )
        }
        getOptionLabel={(option) => option.label ?? ''}
        groupBy={({ type }) => optionsGroupBy(type, t)}
        inputValue={query}
        isOptionEqualToValue={(option, other) => option.value === other.value}
        limitTags={1}
        loading={areCrmOptionsLoading}
        multiple={multiple}
        options={sortedOptions}
        renderOption={(props, option) => {
          const icon = createElement(CrmTypeIcon, { type: option.type ?? 'default' });

          if (option.type === CrmOptionType.Info) {
            return (
              <Alert
                key={option.value}
                className="mx-2 mb-2"
                color="warning"
                size="sm"
                startDecorator={icon}
              >
                <p className="italic">{option.label}</p>
              </Alert>
            );
          }

          if (option.type === CrmOptionType.ActionAdd) {
            return (
              <Button
                key={option.label}
                className="justify-start rounded-none"
                startDecorator={icon}
                variant="plain"
                onClick={() => option.action && option.action()}
              >
                {option.label}
              </Button>
            );
          }

          return (
            <JoyAutocompleteOption {...props} key={`${option.value}-${option.label}`}>
              <ListItemDecorator>{icon}</ListItemDecorator>
              <ListItemContent className="flex flex-wrap items-center gap-2">
                {option.label}
                {(option.type === CrmOptionType.Contact || option.type === CrmOptionType.Company) &&
                  'address' in option.data &&
                  !!option.data.address?.addressLine && (
                    <Typography>({option.data.address.addressLine})</Typography>
                  )}
              </ListItemContent>
            </JoyAutocompleteOption>
          );
        }}
        value={value}
        onChange={(_, newValue) => handleChange(newValue)}
        onInputChange={(_, newInputValue) => setQuery(newInputValue)}
      />
      <AddModal
        sidebar={addInSidebar}
        title={modalToggle.state ? modalTitles[modalToggle.state] : undefined}
        toggle={modalToggle}
      >
        {modalToggle.state === 'centralCompany' && (
          <AddCentralCompanyForm
            onCancel={modalToggle.hide}
            onSubmit={handleCreateCentralCompany}
          />
        )}
        {modalToggle.state === 'company' && (
          <AddEditCompanyForm onCancel={modalToggle.hide} onSubmit={handleCreateCompany} />
        )}
        {modalToggle.state === 'contact' && (
          <AddEditContactForm onCancel={modalToggle.hide} onSubmit={handleCreateContact} />
        )}
      </AddModal>
    </>
  );
};

type _BaseSelectCRMJoyWithIdValuesProps = AdapterProps;

export const BaseSelectCRMJoyWithIdValues: React.FC<_BaseSelectCRMJoyWithIdValuesProps> = (
  props,
) => {
  const autocompleteProps = useIdValueAdapter(props);

  return <BaseSelectCRMJoy {...autocompleteProps} />;
};

type _SelectCRMJoyProps = WithControlProps<_BaseSelectCRMJoyWithIdValuesProps>;

export type SelectCRMJoyProps<T extends boolean = false> = Extract<
  _SelectCRMJoyProps,
  { multiple?: T }
>;

// I have absolutely no clue why a joy and joyC exist
const SelectCRMJoy: React.FC<_SelectCRMJoyProps> = ({
  error,
  className,
  label,
  isRequired,
  ...rest
}) => (
  <FormControl className={classNames('flex-1', className)} error={!!error}>
    {label && <FormLabel required={isRequired}>{label}</FormLabel>}
    <BaseSelectCRMJoyWithIdValues {...rest} />
    {error && <FormHelperText>{error}</FormHelperText>}
  </FormControl>
);

export default SelectCRMJoy;
